import { Logger } from '../support/Logger';
import { ILogger } from '../support/ILogger';

export type IAvatarImageStatus = 'loading' | 'loaded' | 'failed';

/**
 * Loading is injected rather than called directly so the state machine can be exercised
 * without a DOM: `new Image()` does not exist under the unit test runner.
 */
export interface IImageLoader {
    load: (url: string) => Promise<void>;
}

/**
 * Loads off-document, so a URL that fails never reaches the DOM and the browser has nothing
 * to draw a broken-image indicator into. Once this resolves, the image is in the browser
 * cache and the visible element paints on its first frame without a second request.
 */
export class BrowserImageLoader implements IImageLoader {
    load = (url: string): Promise<void> => {
        return new Promise((resolve, reject) => {
            const image = new Image();
            image.onload = () => resolve();
            image.onerror = () => reject(new Error(`could not load ${url}`));
            image.src = url;
        });
    };
}

export interface IAvatarImageRequest {
    url: string;
    onStatus: (status: IAvatarImageStatus) => void;
    timeoutMs?: number;
    retryDelaysMs?: number[];
}

// Short enough that a hung request surfaces as a visible failure quickly. Retries continue after
// that, so a slow network costs a moment of placeholder rather than a long blank circle.
const DEFAULT_TIMEOUT_MS = 3000;
const DEFAULT_RETRY_DELAYS_MS = [1000, 3000];

export class AvatarImageLoader {
    static inject = () => [BrowserImageLoader, Logger];
    constructor(private imageLoader: IImageLoader, private logger: ILogger) {}

    // An image that fails is usually drawn wherever its person appears, so each is reported once.
    private reportedFailures = new Set<string>();

    /**
     * Reports 'loading' immediately, then 'failed' as soon as the first attempt gives up, and
     * 'loaded' if any attempt succeeds. Failing fast and retrying underneath means a transient
     * cold-start failure still self-corrects without the caller holding a blank-looking circle
     * for the whole retry budget.
     *
     * Returns a cancel function that must be called on unmount and whenever the url changes, or
     * a late result from an abandoned request will overwrite newer state.
     */
    load = ({
        url,
        onStatus,
        timeoutMs = DEFAULT_TIMEOUT_MS,
        retryDelaysMs = DEFAULT_RETRY_DELAYS_MS
    }: IAvatarImageRequest): (() => void) => {
        let cancelled = false;

        const emit = (status: IAvatarImageStatus) => {
            if (!cancelled) {
                onStatus(status);
            }
        };

        emit('loading');

        const attempt = async (remainingDelays: number[]): Promise<void> => {
            const loaded = await this.tryOnce(url, timeoutMs);
            if (cancelled) {
                return;
            }
            if (loaded) {
                emit('loaded');
                return;
            }

            emit('failed');

            if (remainingDelays.length === 0) {
                this.reportFailure(url);
                return;
            }

            const [delay, ...rest] = remainingDelays;
            await this.wait(delay);
            if (cancelled) {
                return;
            }
            return attempt(rest);
        };

        // Deliberately unguarded. tryOnce turns a rejected load into `false`, so attempt only
        // rejects if onStatus itself throws -- a defect in the caller, which should surface rather
        // than be reported back through the same throwing callback.
        attempt(retryDelaysMs);

        return () => {
            cancelled = true;
        };
    };

    /**
     * A request that never answers is a failure, not perpetual loading. Without the timeout the
     * caller would sit on the neutral placeholder indefinitely.
     */
    private tryOnce = async (url: string, timeoutMs: number): Promise<boolean> => {
        let timer: ReturnType<typeof setTimeout> | undefined;

        const timedOut = new Promise<boolean>(resolve => {
            timer = setTimeout(() => resolve(false), timeoutMs);
        });
        const loaded = (async () => {
            try {
                await this.imageLoader.load(url);
                return true;
            }
            catch {
                // The rejection carries nothing to act on: an image either painted or it did not.
                // A url that never loads is reported once its retries are exhausted.
                return false;
            }
        })();

        const result = await Promise.race([loaded, timedOut]);
        if (timer) {
            clearTimeout(timer);
        }
        return result;
    };

    // Logger sends nothing while the device is offline, when every image would fail. Without the
    // query string, which can carry a media token.
    private reportFailure = (url: string) => {
        // a local preview carries the picture itself
        if (/^(data|blob):/i.test(url)) {
            return;
        }
        const location = url.split('?')[0];
        if (this.reportedFailures.has(location)) {
            return;
        }
        this.reportedFailures.add(location);
        this.logger.error(new Error('avatar image could not be loaded'), { url: location });
    };

    private wait = (ms: number): Promise<void> => {
        return new Promise(resolve => setTimeout(resolve, ms));
    };
}
